import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { 
  Plus, Calendar, Users, Euro, TrendingUp, 
  LogOut, User, Clock, MapPin, BarChart3, Sparkles,
  Archive, ChevronDown, ChevronUp, CheckCircle, XCircle, AlertCircle
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../i18n';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CoachDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t, lang } = useTranslation();
  
  const [dashboard, setDashboard] = useState(null);
  const [archivedSessions, setArchivedSessions] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [loadingArchived, setLoadingArchived] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await fetch(`${API_URL}/api/coaches/dashboard`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setDashboard(data);
      }
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchArchivedSessions = async () => {
    if (archivedSessions.length > 0) return;
    
    setLoadingArchived(true);
    try {
      const response = await fetch(`${API_URL}/api/sessions/coach/archived`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setArchivedSessions(data);
      }
    } catch (error) {
      console.error('Error fetching archived sessions:', error);
    } finally {
      setLoadingArchived(false);
    }
  };

  const toggleArchived = () => {
    if (!showArchived) {
      fetchArchivedSessions();
    }
    setShowArchived(!showArchived);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const locale = lang === 'nl' ? 'nl-BE' : lang === 'en' ? 'en-GB' : 'fr-FR';
    return date.toLocaleDateString(locale, { 
      weekday: 'short', 
      day: 'numeric', 
      month: 'short' 
    });
  };

  const getArchiveStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'not_realized':
        return <AlertCircle className="w-4 h-4 text-orange-500" />;
      case 'cancelled':
        return <XCircle className="w-4 h-4 text-gray-500" />;
      default:
        return <Archive className="w-4 h-4 text-gray-400" />;
    }
  };

  const getArchiveStatusBadgeClass = (color) => {
    switch (color) {
      case 'green':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'orange':
        return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      case 'gray':
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" data-testid="coach-dashboard-logo">
              <img src="/logo.png" alt="Motivya" className="h-10 w-auto" />
            </Link>
            
            <div className="flex items-center gap-3">
              <Link to="/coach/calendar">
                <Button variant="ghost" className="text-white/70 hover:text-white" data-testid="nav-calendar">
                  <Calendar className="w-4 h-4 mr-2" />
                  <span className="hidden sm:inline">{t('coachDash.calendar')}</span>
                </Button>
              </Link>
              <Link to="/coach/profile">
                <Button variant="ghost" className="text-white/70 hover:text-white" data-testid="nav-profile">
                  <User className="w-4 h-4" />
                </Button>
              </Link>
              <Button variant="ghost" onClick={handleLogout} className="text-white/70 hover:text-white" data-testid="nav-logout">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Welcome */}
        <motion.div 
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
          className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-10"
        >
          <div>
            <h1 className="text-4xl font-bold mb-2">
              {t('coachDash.hello')} <span className="gradient-text">{user?.name?.split(' ')[0]}</span>
            </h1>
            <p className="text-white/50">{t('coachDash.coachSpace')}</p>
          </div>
          <Link to="/coach/sessions/new">
            <Button className="btn-secondary py-4 px-8" data-testid="create-session-btn">
              <Plus className="w-5 h-5 mr-2" />
              {t('coachDash.newSession')}
            </Button>
          </Link>
        </motion.div>

        {/* Stats Cards - Bento Grid */}
        <motion.div 
          initial="hidden"
          animate="visible"
          variants={{
            visible: { transition: { staggerChildren: 0.1 } }
          }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10"
        >
          <motion.div variants={fadeInUp} className="stat-card col-span-2 lg:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-[var(--brand-cyan)]/20 flex items-center justify-center">
                <Calendar className="w-6 h-6 text-[var(--brand-cyan)]" />
              </div>
              <div className="text-sm text-white/50">Total</div>
            </div>
            <div className="stat-value text-[var(--brand-cyan)]">{dashboard?.total_sessions || 0}</div>
            <div className="stat-label">{t('coachDash.sessionsCreated')}</div>
          </motion.div>

          <motion.div variants={fadeInUp} className="stat-card">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-green-400" />
              </div>
            </div>
            <div className="stat-value text-green-400">{dashboard?.upcoming_sessions || 0}</div>
            <div className="stat-label">{t('coachDash.upcoming')}</div>
          </motion.div>

          <motion.div variants={fadeInUp} className="stat-card">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-[var(--brand-yellow)]/20 flex items-center justify-center">
                <Users className="w-6 h-6 text-[var(--brand-yellow)]" />
              </div>
            </div>
            <div className="stat-value text-[var(--brand-yellow)]">{dashboard?.total_bookings || 0}</div>
            <div className="stat-label">{t('coachDash.bookings')}</div>
          </motion.div>

          <motion.div variants={fadeInUp} className="stat-card relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-amber-500/20 to-transparent rounded-full -translate-y-1/2 translate-x-1/2"></div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
                <Euro className="w-6 h-6 text-amber-400" />
              </div>
            </div>
            <div className="stat-value text-amber-400">{dashboard?.total_revenue || 0}€</div>
            <div className="stat-label">{t('coachDash.totalRevenue')}</div>
          </motion.div>
        </motion.div>

        {/* Quick Actions */}
        <motion.div 
          initial="hidden"
          animate="visible"
          variants={{
            visible: { transition: { staggerChildren: 0.1, delayChildren: 0.2 } }
          }}
          className="grid sm:grid-cols-3 gap-4 mb-10"
        >
          <motion.div variants={fadeInUp}>
            <Link to="/coach/sessions/new" className="glass-card p-6 rounded-2xl block group">
              <div className="w-14 h-14 rounded-xl bg-[var(--brand-cyan)]/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Plus className="w-7 h-7 text-[var(--brand-cyan)]" />
              </div>
              <h3 className="font-bold text-lg mb-1">{t('coachDash.createSession')}</h3>
              <p className="text-sm text-white/40">{t('coachDash.publishActivity')}</p>
            </Link>
          </motion.div>
          
          <motion.div variants={fadeInUp}>
            <Link to="/coach/calendar" className="glass-card p-6 rounded-2xl block group">
              <div className="w-14 h-14 rounded-xl bg-green-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Calendar className="w-7 h-7 text-green-400" />
              </div>
              <h3 className="font-bold text-lg mb-1">{t('coachDash.myCalendar')}</h3>
              <p className="text-sm text-white/40">{t('coachDash.managePlanning')}</p>
            </Link>
          </motion.div>
          
          <motion.div variants={fadeInUp}>
            <Link to="/coach/profile" className="glass-card p-6 rounded-2xl block group">
              <div className="w-14 h-14 rounded-xl bg-[var(--brand-yellow)]/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Sparkles className="w-7 h-7 text-[var(--brand-yellow)]" />
              </div>
              <h3 className="font-bold text-lg mb-1">{t('coachDash.myProfile')}</h3>
              <p className="text-sm text-white/40">{t('coachDash.optimizeVisibility')}</p>
            </Link>
          </motion.div>
        </motion.div>

        {/* Recent Sessions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold uppercase">{t('coachDash.recentSessions')}</h2>
            <Link to="/coach/calendar" className="text-[var(--brand-cyan)] text-sm hover:underline">
              {t('coachDash.viewAll')}
            </Link>
          </div>

          {dashboard?.recent_sessions?.length === 0 ? (
            <div className="glass-card rounded-2xl p-12 text-center">
              <Calendar className="w-16 h-16 text-white/20 mx-auto mb-6" />
              <h3 className="font-bold text-xl mb-2">{t('coachDash.noSessions')}</h3>
              <p className="text-white/40 mb-6">{t('coachDash.startFirst')}</p>
              <Link to="/coach/sessions/new">
                <Button className="btn-primary" data-testid="create-first-session">
                  <Plus className="w-5 h-5 mr-2" />
                  {t('coachDash.createSession')}
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {dashboard?.recent_sessions?.map((session, index) => (
                <motion.div 
                  key={session.session_id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                  className="glass-card rounded-2xl p-5 card-hover"
                  data-testid={`session-${session.session_id}`}
                >
                  <div className="flex flex-col sm:flex-row justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          session.status === 'confirmed' ? 'badge-confirmed' : 'badge-pending'
                        }`}>
                          {session.status === 'confirmed' ? t('coachDash.confirmed') : t('coachDash.pending')}
                        </span>
                        {session.is_event && (
                          <span className="badge-event">{t('coachDash.event')}</span>
                        )}
                        <span className="text-xs text-white/40 px-2 py-1 bg-white/5 rounded-full">
                          {session.category}
                        </span>
                      </div>
                      <h3 className="font-bold text-xl mb-3">{session.title}</h3>
                      <div className="flex flex-wrap gap-4 text-sm text-white/50">
                        <span className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-[var(--brand-cyan)]" />
                          {formatDate(session.date)}
                        </span>
                        <span className="flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          {session.start_time}
                        </span>
                        <span className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-[var(--brand-green)]" />
                          <span className="truncate max-w-[200px]">{session.location}</span>
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-[var(--brand-cyan)]">
                          {session.current_participants}/{session.max_participants}
                        </div>
                        <div className="text-xs text-white/40">{t('coachDash.participants')}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-[var(--brand-yellow)]">
                          {session.price}€
                        </div>
                        <div className="text-xs text-white/40">{session.duration_minutes}min</div>
                      </div>
                      <Link to={`/coach/sessions/${session.session_id}/edit`}>
                        <Button variant="outline" className="border-white/20" data-testid={`edit-session-${session.session_id}`}>
                          {t('coachDash.edit')}
                        </Button>
                      </Link>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {/* Section Sessions Archivées */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="mt-8"
          >
            <button
              onClick={toggleArchived}
              className="w-full flex items-center justify-between p-4 glass-card rounded-xl hover:bg-white/5 transition-colors"
              data-testid="toggle-archived-sessions"
            >
              <div className="flex items-center gap-3">
                <Archive className="w-5 h-5 text-white/40" />
                <span className="font-semibold">{t('coachDash.expiredSessions')}</span>
                {archivedSessions.length > 0 && (
                  <span className="px-2 py-0.5 bg-white/10 rounded-full text-xs">
                    {archivedSessions.length}
                  </span>
                )}
              </div>
              {showArchived ? (
                <ChevronUp className="w-5 h-5 text-white/40" />
              ) : (
                <ChevronDown className="w-5 h-5 text-white/40" />
              )}
            </button>

            <AnimatePresence>
              {showArchived && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  {loadingArchived ? (
                    <div className="flex justify-center py-8">
                      <div className="spinner"></div>
                    </div>
                  ) : archivedSessions.length === 0 ? (
                    <div className="text-center py-8 text-white/40">
                      <Archive className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>{t('coachDash.noExpired')}</p>
                    </div>
                  ) : (
                    <div className="space-y-3 mt-4">
                      {archivedSessions.map((session, index) => (
                        <motion.div
                          key={session.session_id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05 }}
                          className="glass-card rounded-xl p-4 opacity-75 hover:opacity-100 transition-opacity"
                          data-testid={`archived-session-${session.session_id}`}
                        >
                          <div className="flex flex-col sm:flex-row justify-between gap-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2 flex-wrap">
                                {getArchiveStatusIcon(session.archive_status)}
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${getArchiveStatusBadgeClass(session.archive_color)}`}>
                                  {session.archive_label}
                                </span>
                                <span className="text-xs text-white/30 px-2 py-0.5 bg-white/5 rounded-full">
                                  {session.category}
                                </span>
                              </div>
                              <h4 className="font-semibold text-white/80 mb-2">{session.title}</h4>
                              <div className="flex flex-wrap gap-3 text-xs text-white/40">
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  {formatDate(session.date)}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {session.start_time}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Users className="w-3 h-3" />
                                  {session.current_participants}/{session.max_participants}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-right">
                                <div className="text-lg font-bold text-white/60">{session.price}€</div>
                                <div className="text-xs text-white/30">
                                  {session.current_participants > 0 
                                    ? `${session.current_participants * session.price}€ total`
                                    : t('coachDash.noRevenue')
                                  }
                                </div>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}
